import React, { useState, useEffect } from 'react';
import { format, addMonths, addWeeks, addYears, parse, isBefore, isAfter, parseISO } from 'date-fns';
import ProgressBar from './ProgressBar';
import ToPeso from './ToPeso';

// Function to round up to the nearest cent if the second decimal place is not zero
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== '0') {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

const calculateDueDates = (startDate, interval, term, amount, interest) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (amount * interest) / 100;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;

  let currentDate = new Date(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case 'monthly':
        currentDate = addMonths(currentDate, 1);
        break;
      case 'bimonthly': // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case 'weekly':
        currentDate = addWeeks(currentDate, 1);
        break;
      case 'quarterly':
        currentDate = addMonths(currentDate, 3);
        break;
      case 'semiannual':
        currentDate = addMonths(currentDate, 6);
        break;
      case 'yearly':
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error('Invalid interval');
    }

    dueDates.push({
      date: format(currentDate, 'MM/dd/yyyy'),
      dueAmount: roundUp(principalAmount + interestAmount)
    });
  }

  return dueDates;
};

const checkPayments = (startDate, dueDate, previousDueDate, paymentList, dueAmount, isLastDueDate) => {
  let totalPaid = 0;
  let status = 'nopayment'; // Default status if no payment is found
  let amountDifference = 0;

  const startDateParsed = previousDueDate ? parse(previousDueDate, 'MM/dd/yyyy', new Date()) : new Date(startDate);
  const endDateParsed = parse(dueDate, 'MM/dd/yyyy', new Date());
  const currentDate = new Date(); // Get current date

  paymentList.forEach((payment) => {
    const paymentDate = parseISO(payment.paymentDate);
    if (isAfter(paymentDate, startDateParsed) && (isBefore(paymentDate, endDateParsed) || paymentDate.getTime() === endDateParsed.getTime() || (isLastDueDate && isAfter(paymentDate, endDateParsed)))) {
      totalPaid += payment.paidTotal;
    }
  });

  if (totalPaid >= dueAmount) {
    status = 'paid';
    amountDifference = totalPaid - dueAmount;
  } else if (totalPaid > 0) {
    status = 'lacking';
    amountDifference = dueAmount - totalPaid;
  } else if (isAfter(currentDate, endDateParsed)) {
    status = 'skipped';
  }

  return { status, amountDifference, totalPaid };
};

const DueDates = ({ date, interval, term, amount, interest, paymentList }) => {
  const dueDates = calculateDueDates(date, interval, term, amount, interest);
  const initialIntrest = (amount * (interest/100));
  const totalRepayments = dueDates.reduce((acc, due) => acc + due.dueAmount, 0) + initialIntrest;

  const [totalPaid, setTotalPaid] = useState(0);
  const [remainingBalance, setRemainingBalance] = useState(totalRepayments);

  useEffect(() => {
    let total = 0;
    paymentList.forEach(payment => {
      total += payment.paidTotal;
    });
    setTotalPaid(total + initialIntrest);
    setRemainingBalance(totalRepayments - total - initialIntrest);
  }, [paymentList, totalRepayments, initialIntrest]);

  return (
    <>
      <div id="payment-duedates">
        <ul>
          {dueDates.map((due, index) => {
            const previousDueDate = index > 0 ? dueDates[index - 1].date : null;
            const isLastDueDate = index === dueDates.length - 1;
            const { status, amountDifference, totalPaid } = checkPayments(date, due.date, previousDueDate, paymentList, due.dueAmount, isLastDueDate);
            
            // Mapping status to text status
            const statusTextMap = {
              'paid': 'Paid',
              'lacking': 'Lacking',
              'skipped': 'Skipped',
              'default': 'No Payment'
            };

            const txtstatus = statusTextMap[status] || statusTextMap['default'];

            return (
              <li key={index} className={status}>
                <span className="term">
                  <div>{index + 1}</div>
                </span>
                <span className="txtdate">{format(due.date, 'MMM dd, yyyy')}</span> (
                <b>
                  <ToPeso amount={due.dueAmount} />
                </b>
                ) <span className={`${status} status`}>{txtstatus}</span>
                {status === 'lacking' && (
                  <>
                    {' '} <span className="lack"><i className="fa-regular fa-circle-down"></i> <ToPeso amount={amountDifference} /></span> <span className="actual"><i className="fa-solid fa-hand-holding-dollar"></i> <ToPeso amount={totalPaid} /></span>
                  </>
                )}
                {status === 'paid' && amountDifference > 0 && (
                  <>
                    {' '} <span className="excess"><i className="fa-regular fa-circle-up"></i> <ToPeso amount={amountDifference} /></span> <span className="actual"><i className="fa-solid fa-hand-holding-dollar"></i> <ToPeso amount={totalPaid} /></span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <ProgressBar totalPayments={totalPaid.toFixed(2)} remainingBalance={remainingBalance.toFixed(2)} totalRepayments={totalRepayments.toFixed(2)} />
    </>
  );
};

export default DueDates;
